import React, { useRef, useState } from 'react'

import { useOutsideClick } from '../../../../../common/useOutsideClick'
import { promptFileDownload } from '../../../../../common/fileUtils'

import { useGetAllFormTemplatesQuery, useGetFilledOutFormTemplateFileMutation } from '../../../../../store/ai/formTemplate'
import { useLazyDownloadGlobalFileRequestQuery } from '../../../../../store/media/file'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'

import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { Button } from '../../../../../components/Button/Button'

import { FormTemplate } from '../../../../../models/FormTemplate'
import { AIAgentResult } from '../../../../../models/AIAgent'
import { Scorecard } from '../../../../../models/Scorecard'

import { TrackEvent, useTracking } from '../../common/tracking'

interface FormResultDownloadButtonProps {
  scorecard?: Scorecard
  formResult: AIAgentResult
}

export const FormResultDownloadButton: React.FC<FormResultDownloadButtonProps> = ({ scorecard, formResult }) => {
  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')

  if (!documentSummaryEnabled) {
    return null
  }

  return <FormResultDownloadButtonContent scorecard={scorecard} formResult={formResult} />
}

export const FormResultDownloadButtonContent: React.FC<FormResultDownloadButtonProps> = ({ scorecard, formResult }) => {
  const accountId = useAppSelector(selectAccountId)

  const { tracked } = useTracking()

  const { data: formTemplatesResponse } = useGetAllFormTemplatesQuery({ accountId, scorecardId: scorecard?.id ?? '' }, { skip: !scorecard })

  const [triggerGetFilledOutFormTemplateFile] = useGetFilledOutFormTemplateFileMutation()
  const [triggerGetFileDownloadInfo] = useLazyDownloadGlobalFileRequestQuery()

  const [showFormTemplateDropdown, setShowFormTemplateDropdown] = useState<boolean>(false)
  const [filledOutFormTemplateFileLoading, setFilledOutFormTemplateFileLoading] = useState<boolean>(false)

  const handleGetFilledOutFormTemplateFile = async () => {
    if (!formTemplatesResponse) {
      return
    }

    const { data: formTemplates } = formTemplatesResponse
    if (!formTemplates.length) {
      return
    }

    if (formTemplates.length > 1) {
      setShowFormTemplateDropdown(true)
      return
    }

    const { id: formTemplateId } = formTemplates[0]
    await getFilledOutFormTemplateFile(formTemplateId)
  }

  const _getFilledOutFormTemplateFile = async (formTemplateId: string) => {
    setFilledOutFormTemplateFileLoading(true)
    try {
      const { id: formResultId } = formResult
      const { data: filledOutFormTemplateFile } = await triggerGetFilledOutFormTemplateFile({ accountId, formResultId, formTemplateId })
      if (!filledOutFormTemplateFile) {
        return null
      }

      const { fileId, fileName } = filledOutFormTemplateFile
      const { data: fileDownloadInfoResponse } = await triggerGetFileDownloadInfo({ accountId, fileId })
      if (!fileDownloadInfoResponse) {
        return
      }

      const { data: fileDownloadInfo } = fileDownloadInfoResponse
      const { location } = fileDownloadInfo

      const response = await fetch(location, { method: 'GET' })
      const fileContent = await response.blob()

      await promptFileDownload(fileContent, fileName)
    } catch (e) {
      console.error(e)
    } finally {
      setFilledOutFormTemplateFileLoading(false)
    }
  }

  const getFilledOutFormTemplateFile = tracked(
    _getFilledOutFormTemplateFile,
    { eventName: TrackEvent.FORM_TEMPLATE_FILLED_OUT },
    {
      eventDetailsExtractor: (formTemplateId: string) => {
        return {
          formTemplateId,
        }
      },
    },
  )

  if (!formTemplatesResponse?.data.length) {
    return null
  }

  return (
    <div className='position-relative'>
      <ScopedBlock
        missingScopeAction={MissingScopeAction.DISABLE}
        scope={{
          scope: 'form_template:read',
          superScope: ['form_template:admin'],
        }}
      >
        <Button className='px-2 py-1 button-medium' variant='header' onClick={handleGetFilledOutFormTemplateFile} loading={filledOutFormTemplateFileLoading}>
          Download
        </Button>
      </ScopedBlock>
      {showFormTemplateDropdown ? (
        <FormTemplateDropdown
          formTemplates={formTemplatesResponse?.data ?? []}
          onHide={() => setShowFormTemplateDropdown(false)}
          onFormTemplateSelected={async (formTemplateId: string) => {
            setShowFormTemplateDropdown(false)
            await getFilledOutFormTemplateFile(formTemplateId)
          }}
        />
      ) : null}
    </div>
  )
}

interface FormTemplateDropdownProps {
  formTemplates: FormTemplate[]
  onHide: () => void
  onFormTemplateSelected: (formTemplateId: string) => void
}

const FormTemplateDropdown: React.FC<FormTemplateDropdownProps> = ({ formTemplates, onFormTemplateSelected, onHide }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(containerRef, () => {
    onHide()
  })

  return (
    <div ref={containerRef} className='d-flex flex-column align-items-start gap-3 p-3 rounded-4 zocks-bg-color-white form-template-dropdown position-absolute'>
      <span className='lead-extra-small zocks-text-gray-700'>Pick a document template</span>
      {formTemplates.map((formTemplate) => (
        <div className='d-flex align-items-center gap-1 cursor-pointer' onClick={() => onFormTemplateSelected(formTemplate.id)}>
          <i className='bi bi-file-pdf fs-6' />
          <span className='paragraph-medium bold'>{formTemplate.name}</span>
        </div>
      ))}
    </div>
  )
}
